import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaClipboardList, FaGraduationCap, FaCheckCircle, FaArrowLeft, FaBook } from 'react-icons/fa';

interface ClassItem {
  id: number;
  name: string;
}

interface Section {
  id: number;
  name: string;
  class_associated: number;
}

interface Student {
  id: number;
  name: string;
  section_associated: number;
}

interface Examination {
  id: number;
  exam_name: string;
  exam_type: string;
}

interface Subject {
  id: number;
  name: string;
}

interface SubjectMark {
  subject: number;
  single_exam_marks: string;
}

const AddMark: React.FC = () => {
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [allSections, setAllSections] = useState<Section[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [filteredSections, setFilteredSections] = useState<Section[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [examinations, setExaminations] = useState<Examination[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const [selectedSection, setSelectedSection] = useState<number | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<number | null>(null);
  const [selectedExam, setSelectedExam] = useState<number | null>(null);
  const [subjectMarks, setSubjectMarks] = useState<SubjectMark[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [userRole, setUserRole] = useState<string>('');
  const auth = useSelector((state: RootState) => state.auth.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const token = getCookie('token');
        if (!token) {
          dispatch(setAuth(false));
          toast.error('No authentication token found. Please log in.');
          return;
        }
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const { data } = await axios.get('/users/user');
        setUserRole(data.user.role);
        dispatch(setAuth(true));
      } catch (error) {
        console.error('Error fetching user data:', error);
        dispatch(setAuth(false));
        toast.error('Failed to fetch user data');
      }
    };

    const fetchClasses = async () => {
      try {
        const token = getCookie('token');
        const response = await axios.get('/Branches/classes/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClasses(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching classes:', error);
        toast.error('Failed to fetch classes');
      }
    };

    const fetchAllSections = async () => {
      try {
        const token = getCookie('token');
        const response = await axios.get('/Branches/sections/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllSections(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching sections:', error);
        toast.error('Failed to fetch sections');
      }
    };

    const fetchAllStudents = async () => {
      try {
        const token = getCookie('token');
        const response = await axios.get('/Branches/students/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAllStudents(Array.isArray(response.data.results) ? response.data.results : []);
      } catch (error) {
        console.error('Error fetching students:', error);
        toast.error('Failed to fetch students');
      }
    };

    const fetchExaminations = async () => {
      try {
        const token = getCookie('token');
        const response = await axios.get('/exams/exams/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setExaminations(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching examinations:', error);
        toast.error('Failed to fetch examinations');
      }
    };

    const fetchSubjects = async () => {
      try {
        const token = getCookie('token');
        const response = await axios.get('/exams/subjects/', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const subjectsData = Array.isArray(response.data) ? response.data : [];
        setSubjects(subjectsData);
        setSubjectMarks(subjectsData.map((subj: Subject) => ({
          subject: subj.id,
          single_exam_marks: '',
        })));
      } catch (error) {
        console.error('Error fetching subjects:', error);
        toast.error('Failed to fetch subjects');
      }
    };

    const fetchData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUserData(),
        fetchClasses(),
        fetchAllSections(),
        fetchAllStudents(),
        fetchExaminations(),
        fetchSubjects()
      ]);
      setLoading(false);
    };

    fetchData();
  }, [dispatch]);

  useEffect(() => {
    if (selectedClass) {
      const filtered = allSections.filter(section => section.class_associated === selectedClass);
      setFilteredSections(filtered);
    } else {
      setFilteredSections([]);
    }
    setSelectedSection(null);
    setSelectedStudent(null);
  }, [selectedClass, allSections]);

  useEffect(() => {
    if (selectedSection) {
      const filtered = allStudents.filter(student => student.section_associated === selectedSection);
      setFilteredStudents(filtered);
    } else {
      setFilteredStudents([]);
    }
    setSelectedStudent(null);
  }, [selectedSection, allStudents]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedClass || !selectedSection || selectedStudent === null || !selectedExam) {
      toast.error('Please select Class, Section, Student, and Examination');
      return;
    }

    const validMarks = subjectMarks.filter((sm) => sm.single_exam_marks !== '');
    if (validMarks.length === 0) {
      toast.error('Please enter marks for at least one subject');
      return;
    }

    const hasInvalidMarks = validMarks.some((sm) => {
      const marks = parseFloat(sm.single_exam_marks);
      return isNaN(marks) || marks < 0 || marks > 100;
    });

    if (hasInvalidMarks) {
      toast.error('Exam marks must be between 0 and 100');
      return;
    }

    setLoading(true);
    try {
      const token = getCookie('token');
      if (!token) {
        toast.error('No authentication token found. Please log in.');
        setLoading(false);
        return;
      }

      for (const sm of validMarks) {
        const payload = {
          student_id: selectedStudent,
          exam_id: selectedExam,
          subject_id: sm.subject,
          single_exam_marks: parseFloat(sm.single_exam_marks),
        };
        await axios.post(
          '/exams/marks/',
          payload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      toast.success('Marks added successfully! Redirecting...');
      setSelectedClass(null);
      setSelectedSection(null);
      setSelectedStudent(null);
      setSelectedExam(null);
      setSubjectMarks(
        subjects.map((subj) => ({
          subject: subj.id,
          single_exam_marks: '',
        }))
      );
      setTimeout(() => {
        navigate('/marks');
      }, 2000);
    } catch (error: any) {
      console.error('Error creating marks:', error.response ? error.response.data : error.message);
      toast.error(error.response?.data?.detail || 'Failed to add marks');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!auth) {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <h4 className="text-muted mb-3">You are not authenticated</h4>
          <a href="/login" className="btn btn-primary px-4 py-2">Go to Login</a>
        </div>
      </div>
    );
  }

  if (userRole !== 'admin' && userRole !== 'super_admin') {
    return (
      <div className="container-fluid min-vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="text-center">
          <h4 className="text-muted mb-3">You are not authorized to view this page</h4>
          <a href="/dashboard" className="btn btn-primary px-4 py-2">Go to Dashboard</a>
        </div>
      </div>
    );
  }

  const progress = [
    { label: 'Class', completed: selectedClass !== null },
    { label: 'Section', completed: selectedSection !== null },
    { label: 'Student', completed: selectedStudent !== null },
    { label: 'Exam', completed: selectedExam !== null },
  ];

  const completedSteps = progress.filter(p => p.completed).length;

  return (
    <div className="dashboard-content">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header Section */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaClipboardList className="me-2 text-primary" />
            Enter Student Marks
          </h2>
          <p className="text-muted mb-0">Record examination marks for students</p>
        </div>
        <button className="btn btn-outline-secondary mt-3 mt-md-0 d-flex align-items-center" onClick={() => navigate('/marks')}>
          <FaArrowLeft className="me-2" /> Back to Marks
        </button>
      </div>

      {/* Progress Indicator */}
      <div className="card-modern p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          {progress.map((step, index) => (
            <React.Fragment key={step.label}>
              <div className="d-flex flex-column align-items-center flex-fill">
                <div className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${step.completed ? 'bg-success text-white' : 'bg-light text-muted'}`} style={{ width: '40px', height: '40px' }}>
                  {step.completed ? <FaCheckCircle /> : index + 1}
                </div>
                <small className="fw-semibold">{step.label}</small>
              </div>
              {index < progress.length - 1 && (
                <div className="flex-fill" style={{ height: '2px', background: step.completed && progress[index + 1].completed ? '#10b981' : '#e5e7eb', margin: '0 8px' }} />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="progress" style={{ height: '8px' }}>
          <div className="progress-bar bg-success" style={{ width: `${(completedSteps / progress.length) * 100}%` }}></div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          <div className="card-modern overflow-hidden">
            <div className="px-5 py-4 border-bottom" style={{ background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' }}>
              <h4 className="text-primary fw-bold mb-0">
                <FaGraduationCap className="me-2" />
                Marks Entry Form
              </h4>
            </div>
            <div className="card-body p-5">
              <form onSubmit={handleSubmit}>
                {/* Selection Row */}
                <div className="row g-4 mb-5">
                  <div className="col-md-3">
                    <label htmlFor="class" className="form-label fw-bold text-secondary">
                      Class <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-control-lg bg-light border-0"
                      id="class"
                      value={selectedClass || ''}
                      onChange={(e) => setSelectedClass(e.target.value ? parseInt(e.target.value) : null)}
                      required
                    >
                      <option value="">Select Class</option>
                      {classes.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>{classItem.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="section" className="form-label fw-bold text-secondary">
                      Section <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-control-lg bg-light border-0"
                      id="section"
                      value={selectedSection || ''}
                      onChange={(e) => setSelectedSection(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={!selectedClass}
                      required
                    >
                      <option value="">Select Section</option>
                      {filteredSections.map((section) => (
                        <option key={section.id} value={section.id}>{section.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="student" className="form-label fw-bold text-secondary">
                      Student <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-control-lg bg-light border-0"
                      id="student"
                      value={selectedStudent || ''}
                      onChange={(e) => setSelectedStudent(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={!selectedSection}
                      required
                    >
                      <option value="">Select Student</option>
                      {filteredStudents.map((student) => (
                        <option key={student.id} value={student.id}>{student.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="examination" className="form-label fw-bold text-secondary">
                      Examination <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select form-control-lg bg-light border-0"
                      id="examination"
                      value={selectedExam || ''}
                      onChange={(e) => setSelectedExam(e.target.value ? parseInt(e.target.value) : null)}
                      required
                    >
                      <option value="">Select Exam</option>
                      {examinations.map((exam) => (
                        <option key={exam.id} value={exam.id}>{exam.exam_name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Subjects Marks Grid */}
                {selectedExam && subjects.length > 0 && (
                  <div className="mb-5">
                    <div className="d-flex align-items-center mb-4 border-bottom pb-3">
                      <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                        <FaBook />
                      </div>
                      <h5 className="fw-bold mb-0">Enter Subject Marks</h5>
                    </div>
                    <div className="row g-4">
                      {subjects.map((subj, index) => {
                        const markValue = subjectMarks[index]?.single_exam_marks || '';
                        const numMark = markValue ? parseFloat(markValue) : null;
                        const isValid = numMark !== null && numMark >= 0 && numMark <= 100;
                        const isEmpty = markValue === '';

                        return (
                          <div key={subj.id} className="col-12 col-md-6 col-lg-4">
                            <div className={`card h-100 border-2 ${!isEmpty && isValid ? 'border-success' : !isEmpty && !isValid ? 'border-danger' : 'border'}`} style={{ transition: 'all 0.3s ease' }}>
                              <div className="card-body text-center p-4">
                                <h6 className="card-title fw-bold text-primary mb-3">
                                  {subj.name}
                                </h6>
                                <input
                                  type="number"
                                  name="single_exam_marks"
                                  className="form-control form-control-lg text-center fw-bold"
                                  placeholder="0 - 100"
                                  value={markValue}
                                  onChange={(e) => {
                                    const updatedMarks = [...subjectMarks];
                                    updatedMarks[index] = { ...updatedMarks[index], single_exam_marks: e.target.value };
                                    setSubjectMarks(updatedMarks);
                                  }}
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  style={{ fontSize: '1.5rem', borderRadius: '12px' }}
                                />
                                <small className="text-muted mt-2 d-block">
                                  {!isEmpty && isValid && '✓ Valid'} enter 0-100
                                </small>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-5 border-top pt-4">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg w-100 shadow-lg"
                    disabled={loading || selectedStudent === null}
                    style={{ borderRadius: '12px', padding: '15px' }}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Adding Marks...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle className="me-2" />
                        Submit Marks
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddMark;